import { toFixedInt } from '@/_utils/to-fixed-int';
import { Insurer } from '@/codebook/insurer/_types/insurer.type';
import { InsurerApiService } from '@/codebook/insurer/insurer.api.service';
import { RESIGNATION_SERVICE_TYPE } from '@/services/_constants/resignation-service-type';
import { Component, inject, Injector, input, OnDestroy, OnInit } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { DataBagService } from 'src/app/common/data-bag.service';
import { InfoboxService } from 'src/app/common/infobox/infobox.service';
import { ServiceCountingMethods } from '../../service.model';
import { ServiceService } from '../../service.service';
import { ServiceDialogComponent } from '../service-dialog/service-dialog.component';

@Component({
	selector: 'app-service-basic',
	templateUrl: './service-basic.component.html',
	styleUrls: ['./service-basic.component.scss'],
})
export class ServiceBasicComponent implements OnInit, OnDestroy {
	readonly editable = input.required<boolean>();

	readonly ServiceCountingMethods = ServiceCountingMethods;
	readonly RESIGNATION_SERVICE_TYPE = RESIGNATION_SERVICE_TYPE;

	readonly service = inject(ServiceService);
	readonly infobox = inject(InfoboxService);
	readonly dialog = inject(MatDialog);
	readonly bag = inject(DataBagService);
	readonly insurerApiService = inject(InsurerApiService);

	readonly insurerFormField = new FormControl<Insurer['id']>(null);

	readonly insurers$: Observable<Insurer[]> = this.insurerApiService
		.getInsurers(0, 999)
		.pipe(map((response) => response.results));

	private readonly injector = inject(Injector);
	private readonly destroy$ = new Subject<void>();

	ngOnInit(): void {
		this.service.dataLoaded$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			if (this.service.data.serviceType === RESIGNATION_SERVICE_TYPE) {
				this.insurerFormField.patchValue(this.service.data.insurer);
			}
		});

		this.insurerFormField.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((insurerId: number) => {
			this.selectInsurer(insurerId);
		});

		toObservable(this.editable, { injector: this.injector })
			.pipe(takeUntil(this.destroy$))
			.subscribe((value) => {
				if (value) {
					this.insurerFormField.enable();
				} else {
					this.insurerFormField.disable();
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	displayWith(insurer: Insurer): string {
		return insurer?.name || '';
	}

	selectInsurer(insurerId: Insurer['id']): void {
		this.service.data.insurer = insurerId;
	}

	protected clear(field, bound): void {
		this.service.data[field][bound] = null;
	}

	changeNumber(price): number {
		return toFixedInt(price * 100, 0);
	}

	changePercent(value): number {
		return toFixedInt(value * 10, 0);
	}

	convertPriceByKind(value: number, kind: string): number {
		if (kind === 'N') return 0;
		if (kind === 'V') return value * 10;
		return value / 10;
	}

	openDialog(): void {
		const dialogRef = this.dialog.open(ServiceDialogComponent, {});

		dialogRef.afterClosed().subscribe((result) => {
			if (!result) {
				return;
			}
			this.bag.post('/offer/service_type/', { name: result }, (result, error) => {
				if (error) {
					return;
				}
				this.service.mockData.service_type.push(result);
				this.infobox.success('Zapisano');
			});
		});
	}
}
